import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getUserByPhone, saveUser, updateReferralCounts } from '../../utils/sharedStorage';

const ADMIN_NUMBERS = ['97800237', '92252664'];

export default function Inscription() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    country: '',
    phone: '',
    referralUsername: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [pendingUser, setPendingUser] = useState<Record<string, unknown> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralUsername: refCode }));
    }
  }, [searchParams]);

  const countries = [
    "Côte d'Ivoire", 'Sénégal', 'Bénin', 'Mali', 'Congo Kinshasa',
    'Togo', 'Burkina Faso', 'Gabon', 'Cameroun', 'Congo Brazzaville', 'Ghana',
  ];

  const countryDialCodes: Record<string, string> = {
    "Côte d'Ivoire": '+225', 'Sénégal': '+221', 'Bénin': '+229',
    'Mali': '+223', 'Congo Kinshasa': '+243', 'Togo': '+228',
    'Burkina Faso': '+226', 'Gabon': '+241', 'Cameroun': '+237',
    'Congo Brazzaville': '+242', 'Ghana': '+233',
  };

  const countryToCode: Record<string, string> = {
    "Côte d'Ivoire": 'CI', 'Sénégal': 'SN', 'Bénin': 'BJ',
    'Mali': 'ML', 'Congo Kinshasa': 'CD', 'Togo': 'TG',
    'Burkina Faso': 'BF', 'Gabon': 'GA', 'Cameroun': 'CM',
    'Congo Brazzaville': 'CG', 'Ghana': 'GH',
  };

  const getDialCode = () => (formData.country ? countryDialCodes[formData.country] : '');

  const inputClass = (field: string) =>
    `flex items-center rounded-xl border-2 transition-all duration-200 ${
      focusedField === field
        ? 'border-red-500 bg-white shadow-lg shadow-red-50'
        : 'border-gray-200 bg-white'
    }`;

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.username.trim()) newErrors.username = "Nom d'utilisateur requis";
    if (!formData.country) newErrors.country = 'Pays requis';
    if (!formData.phone.trim()) newErrors.phone = 'Numéro requis';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    setGlobalError('');
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalError('');
    setErrors({});

    if (formData.password !== formData.confirmPassword) {
      setErrors(prev => ({ ...prev, password: 'Les mots de passe ne correspondent pas' }));
      return;
    }
    if (formData.password.length < 6) {
      setErrors(prev => ({ ...prev, password: 'Minimum 6 caractères' }));
      return;
    }
    if (!formData.acceptTerms) {
      setErrors(prev => ({ ...prev, terms: 'Veuillez accepter les conditions générales' }));
      return;
    }

    const phoneClean = formData.phone.trim().replace(/\s+/g, '');
    setIsSaving(true);

    try {
      // ✅ Vérification doublon dans Supabase (prioritaire)
      const existingUser = await getUserByPhone(phoneClean);
      if (existingUser && existingUser.password) {
        setGlobalError('Ce numéro est déjà enregistré. Veuillez vous connecter.');
        setIsSaving(false);
        return;
      }

      const isSuperAdmin = ADMIN_NUMBERS.includes(phoneClean);
      const invitationCode = formData.username
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9]/g, '');

      const userData = {
        name: formData.username,
        phone: phoneClean,
        email: formData.email,
        country: countryToCode[formData.country] || formData.country,
        countryName: formData.country,
        password: formData.password,
        username: invitationCode,
        accountStatus: (isSuperAdmin ? 'active' : 'pending') as 'active' | 'pending',
        isActivated: isSuperAdmin,
        activated: isSuperAdmin,
        registrationDate: new Date().toISOString(),
        balance: 0,
        points: 0,
        totalWithdrawals: 0,
        referralCode: invitationCode,
        referredBy: formData.referralUsername || null,
        level1Referrals: 0,
        level2Referrals: 0,
        level3Referrals: 0,
      };

      // ✅ Sauvegarder dans Supabase (prioritaire) + localStorage (cache)
      await saveUser(userData as any);

      // ✅ Synchroniser localStorage pour l'utilisateur actuel
      const userDataStr = JSON.stringify(userData);
      localStorage.setItem(`user_${phoneClean}`, userDataStr);
      localStorage.setItem('userPhone', phoneClean);
      localStorage.setItem('userName', formData.username);
      localStorage.setItem('userCountry', formData.country);
      localStorage.setItem('userUsername', invitationCode);
      localStorage.setItem('currentUser', userDataStr);

      // ✅ Mettre à jour les compteurs de parrainage dans Supabase
      if (formData.referralUsername) {
        try { 
          await updateReferralCounts(formData.referralUsername); 
        } catch (err) { 
          console.warn('Erreur mise à jour parrainage:', err);
        }
      }

      setIsSaving(false);

      if (isSuperAdmin) {
        navigate('/tableau-de-bord');
        return;
      }

      setGeneratedCode(invitationCode);
      setPendingUser(userData);
      setShowCodeModal(true);
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      setGlobalError('Une erreur inattendue est survenue. Veuillez réessayer.');
      setIsSaving(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generatedCode).catch(() => {
      const el = document.createElement('textarea');
      el.value = generatedCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleContinueToPayment = () => {
    navigate('/paiement-activation', { state: { country: pendingUser?.country } });
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ===== MODAL CODE D'INVITATION ===== */}
      {showCodeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-2xl mx-auto mb-4">
              <i className="ri-shield-check-line text-3xl text-green-600"></i>
            </div>
            <h2 className="text-xl font-black text-gray-900 mb-1">Inscription réussie !</h2>
            <p className="text-sm text-gray-400 mb-5">Votre code d'invitation unique :</p>

            <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl px-6 py-4 mb-3">
              <p className="text-xs text-gray-400 font-semibold mb-1 uppercase tracking-widest">Code d'invitation</p>
              <p className="text-2xl font-black text-gray-900 tracking-widest">{generatedCode}</p>
            </div>

            <button
              onClick={handleCopyCode}
              className="w-full mb-3 flex items-center justify-center gap-2 font-bold py-2.5 rounded-xl transition cursor-pointer whitespace-nowrap text-sm text-white"
              style={{ background: 'linear-gradient(135deg, #ea580c, #dc2626)' }}
            >
              <i className={copiedCode ? 'ri-check-line' : 'ri-file-copy-line'}></i>
              {copiedCode ? 'Code copié !' : 'Copier mon code'}
            </button>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-left">
              <p className="text-xs text-amber-800 font-semibold mb-0.5">⚠️ Notez ce code !</p>
              <p className="text-xs text-amber-700">Partagez-le avec vos proches pour gagner des commissions.</p>
            </div>

            <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-5">
              <p className="text-xs text-red-700 font-semibold">🔒 Compte en attente d'activation</p>
              <p className="text-xs text-red-500 mt-0.5">Payez les frais d'activation pour accéder à votre tableau de bord.</p>
            </div>

            <button
              onClick={handleContinueToPayment}
              className="w-full text-white font-bold py-3 rounded-xl transition shadow-lg cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm"
              style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}
            >
              <i className="ri-secure-payment-line text-base"></i>
              Continuer vers le paiement
            </button>
          </div>
        </div>
      )}

      {/* ===== PANNEAU GAUCHE ===== */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col items-center justify-center p-12"
        style={{ background: 'linear-gradient(145deg, #1a0a0a 0%, #3d0c0c 40%, #7f1d1d 100%)' }}>

        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-100px] right-[-100px] w-80 h-80 rounded-full border border-red-800/30"></div>
          <div className="absolute top-[-60px] right-[-60px] w-56 h-56 rounded-full border border-red-700/20"></div>
          <div className="absolute bottom-[-80px] left-[-80px] w-72 h-72 rounded-full border border-red-800/30"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-red-900/20"></div>
          <div className="absolute top-1/3 left-[-40px] w-24 h-24 rounded-full bg-orange-900/20"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '32px 32px'
          }}></div>
        </div>

        <div className="relative z-10 text-center w-full max-w-sm">
          {/* Logo */}
          <div className="relative mx-auto mb-8 w-24 h-24">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/30 to-orange-500/20 blur-xl"></div>
            <div className="relative w-24 h-24 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/10 shadow-2xl">
              <img
                src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
                alt="SUCCESSLINK AGENCY"
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>

          <h1 className="text-3xl font-black text-white mb-1 tracking-tight">SUCCESSLINK</h1>
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/30 rounded-full px-4 py-1 mb-10">
            <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></div>
            <span className="text-red-300 text-xs font-semibold tracking-widest uppercase">Agency</span>
          </div>

          <div className="space-y-3 text-left">
            {[
              { icon: 'ri-gift-line', title: 'Rejoignez la communauté', desc: 'Des milliers de membres actifs' },
              { icon: 'ri-money-dollar-circle-line', title: 'Revenus quotidiens', desc: 'Gains garantis chaque jour' },
              { icon: 'ri-team-line', title: 'Parrainage lucratif', desc: 'Commissions sur 3 niveaux' },
              { icon: 'ri-shield-check-line', title: 'Plateforme sécurisée', desc: 'Données 100% protégées' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 rounded-xl px-4 py-3 bg-white/5 border border-white/8 hover:bg-white/8 transition-all duration-200">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gradient-to-br from-red-500/30 to-orange-500/20 flex-shrink-0">
                  <i className={`${item.icon} text-red-300 text-base`}></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{item.title}</p>
                  <p className="text-white/40 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="text-white/30 text-xs">© 2025 SUCCESSLINK AGENCY</p>
          </div>
        </div>
      </div>

      {/* ===== PANNEAU DROIT — FORMULAIRE ===== */}
      <div className="w-full lg:w-[55%] flex flex-col items-center justify-center min-h-screen px-6 py-10"
        style={{ background: '#fafafa' }}>

        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #dc2626, #ea580c)' }}>
            <img
              src="https://static.readdy.ai/image/82792080bea9235a3a5c58a151cce2b4/fff4bb4e89a9e983dddf5fffb9dae068.png"
              alt="SUCCESSLINK AGENCY"
              className="w-10 h-10 object-contain"
            />
          </div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight">SUCCESSLINK</h1>
          <p className="text-gray-400 text-xs font-medium">AGENCY</p>
        </div>

        <div className="w-full max-w-[420px]">
          {/* En-tête */}
          <div className="mb-6">
            <h2 className="text-2xl font-black text-gray-900 mb-1">Créer un compte 🚀</h2>
            <p className="text-gray-400 text-sm">Rejoignez la communauté SUCCESSLINK</p>
          </div>

          {/* Indicateur d'étapes */}
          <div className="flex items-center gap-2 mb-1">
            <div className={`flex-1 h-1 rounded-full transition-all duration-300 ${step >= 1 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
            <div className={`flex-1 h-1 rounded-full transition-all duration-300 ${step >= 2 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
          </div>
          <p className="text-xs text-gray-300 mb-5">Étape {step} / 2</p>

          {globalError && (
            <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-fill text-red-500 text-lg"></i>
              </div>
              <p className="text-sm text-red-700 font-medium">{globalError}</p>
            </div>
          )}

          {/* ===== ÉTAPE 1 ===== */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Nom d'utilisateur */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Nom d'utilisateur</label>
                <div className={inputClass('username')}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    onFocus={() => setFocusedField('username')}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 py-3.5 pr-4 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                    placeholder="Votre nom d'utilisateur"
                  />
                </div>
                <p className="text-xs text-orange-500 mt-1.5 ml-1 font-medium">
                  🎫 Votre code de parrainage sera généré à partir de ce nom
                </p>
                {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Email <span className="text-gray-300 font-normal normal-case">(optionnel)</span>
                </label>
                <div className={inputClass('email')}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 py-3.5 pr-4 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              {/* Pays */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Pays</label>
                <div className={inputClass('country')}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-gray-400 text-lg"></i>
                  </div>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value, phone: '' })}
                    onFocus={() => setFocusedField('country')}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 py-3.5 pr-4 bg-transparent outline-none text-sm text-gray-800 cursor-pointer"
                  >
                    <option value="">Sélectionner votre pays</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Numéro de téléphone</label>
                <div className="flex items-center gap-2">
                  <div className="px-3 py-3.5 bg-white border-2 border-gray-200 rounded-xl text-sm font-bold text-gray-500 min-w-[72px] text-center">
                    {getDialCode() || '+XXX'}
                  </div>
                  <div className={`flex-1 flex items-center rounded-xl border-2 transition-all duration-200 ${
                    focusedField === 'phone' ? 'border-red-500 bg-white shadow-lg shadow-red-50' : 'border-gray-200 bg-white'
                  }`}>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      className="flex-1 px-4 py-3.5 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                      placeholder={formData.country ? 'Votre numéro' : "Choisissez un pays d'abord"}
                      disabled={!formData.country}
                    />
                  </div>
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Code parrainage */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Code d'invitation <span className="text-gray-300 font-normal normal-case">(optionnel)</span>
                </label>
                <div className={inputClass('referral')}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-coupon-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type="text"
                    value={formData.referralUsername}
                    onChange={(e) => setFormData({ ...formData, referralUsername: e.target.value })}
                    onFocus={() => setFocusedField('referral')}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 py-3.5 pr-4 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                    placeholder="Code de votre parrain"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleNextStep}
                className="w-full text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm mt-2"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}
              >
                Continuer
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          )}

          {/* ===== ÉTAPE 2 ===== */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Mot de passe */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Mot de passe</label>
                <div className={inputClass('password')}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-password-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 py-3.5 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                    placeholder="Minimum 6 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer transition"
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
              </div>

              {/* Confirmer mot de passe */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Confirmer le mot de passe</label>
                <div className={inputClass('confirmPassword')}>
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-2-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    className="flex-1 py-3.5 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-300"
                    placeholder="Répétez votre mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer transition"
                  >
                    <i className={showConfirmPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              {/* Conditions */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div
                  onClick={() => setFormData({ ...formData, acceptTerms: !formData.acceptTerms })}
                  className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition cursor-pointer ${
                    formData.acceptTerms ? 'bg-red-600 border-red-600' : 'border-gray-300 group-hover:border-red-400'
                  }`}
                >
                  {formData.acceptTerms && <i className="ri-check-line text-white text-xs"></i>}
                </div>
                <span className="text-sm text-gray-500">J'accepte les conditions générales d'utilisation</span>
              </label>
              {errors.terms && <p className="text-red-500 text-xs">{errors.terms}</p>}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-shrink-0 px-5 py-4 border-2 border-gray-200 text-gray-500 font-bold rounded-xl hover:bg-gray-50 transition cursor-pointer whitespace-nowrap flex items-center gap-2 text-sm bg-white"
                >
                  <i className="ri-arrow-left-line"></i>
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-60 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-sm"
                  style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}
                >
                  {isSaving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Inscription...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line text-lg"></i>
                      S'inscrire
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Séparateur */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-300 font-medium px-2">OU</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Lien connexion */}
          <Link
            to="/connexion"
            className="w-full flex items-center justify-center gap-2 py-3.5 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:border-red-200 hover:text-red-600 hover:bg-red-50/50 transition-all duration-200 cursor-pointer text-sm"
          >
            <i className="ri-login-circle-line text-base"></i>
            Déjà un compte ? Se connecter
          </Link>

          <p className="text-center text-xs text-gray-300 mt-8 flex items-center justify-center gap-1.5">
            <i className="ri-shield-check-line"></i>
            Inscription sécurisée — Données protégées
          </p>
        </div>
      </div>
    </div>
  );
}